// Client-side helpers for partner connections + privacy + meal-day summaries.
// Reads/writes go through Supabase RLS where possible (list, disconnect,
// privacy update), and through Pages Functions where server-side validation
// matters (invite + accept — they need the service role to look up auth
// users + auto-issue codes + send emails).

import { supabase } from "./supabase";
import { apiFetch } from "./apiFetch";

export type PrivacyTier = "vitals" | "vitals_nutrients" | "plus_meal_names" | "full";

export const PRIVACY_LABELS: Record<PrivacyTier, string> = {
  vitals: "Vitals only",
  vitals_nutrients: "Vitals + nutrients",
  plus_meal_names: "+ Meal names",
  full: "Full visibility"
};

export const PRIVACY_DESCRIPTIONS: Record<PrivacyTier, string> = {
  vitals: "Daily calorie ring + macro bars only",
  vitals_nutrients: "Above + the 25-nutrient panel",
  plus_meal_names: "Above + names of meals you log (no portions or photos)",
  full: "Everything except photos — items, portions, weight"
};

export const PRIVACY_TIERS: PrivacyTier[] = [
  "vitals",
  "vitals_nutrients",
  "plus_meal_names",
  "full"
];

/**
 * A partner connection as the calling user sees it. Normalizes the
 * underlying canonical-order (user_a/user_b) into "me vs them" so screens
 * don't have to think about which slot the caller occupies.
 */
export interface Connection {
  id: string;
  status: "pending" | "accepted" | "blocked";
  /** User ID of the other side (null if invite is pending and they don't have an account yet). */
  partnerId: string | null;
  /** Email of the partner — known either via auth.users or via pending_partner_email. */
  partnerEmail: string;
  /** What the caller is exposing to the partner. */
  iShare: PrivacyTier;
  /** What the partner is exposing to the caller. */
  theyShare: PrivacyTier;
  /** True if the caller is the one who sent the invite (vs the recipient). */
  initiatedByMe: boolean;
  createdAt: string;
  acceptedAt: string | null;
}

interface RawConnection {
  id: string;
  user_a: string;
  user_b: string | null;
  a_privacy: PrivacyTier;
  b_privacy: PrivacyTier;
  status: "pending" | "accepted" | "blocked";
  initiated_by: string;
  pending_partner_email: string | null;
  created_at: string;
  accepted_at: string | null;
}

// Per-session partner email cache. Populated by the partner_emails() RPC
// which uses SECURITY DEFINER to fetch auth.users emails for only the
// caller's accepted-partner user IDs. Without this, partner emails would
// vanish once we clear pending_partner_email on accept.
const emailCache = new Map<string, string>();

async function refreshEmailCache(): Promise<void> {
  if (!supabase) return;
  const { data, error } = await supabase.rpc("partner_emails");
  if (error) {
    // Non-fatal: we just fall back to "user XXXX" labels.
    console.warn("partner_emails RPC failed:", error.message);
    return;
  }
  for (const row of (data ?? []) as Array<{ user_id: string; email: string }>) {
    if (row.user_id && row.email) emailCache.set(row.user_id, row.email);
  }
}

/**
 * Tag a connection with which side the current user is on, so callers
 * don't have to think about it.
 */
function normalize(raw: RawConnection, myId: string): Connection {
  const meIsA = raw.user_a === myId;
  const meIsB = raw.user_b === myId;
  const partnerId = meIsA ? raw.user_b : meIsB ? raw.user_a : null;
  const partnerEmail =
    raw.pending_partner_email ??
    (partnerId ? emailCache.get(partnerId) ?? `user ${partnerId.slice(0, 8)}` : "unknown");

  return {
    id: raw.id,
    status: raw.status,
    partnerId,
    partnerEmail,
    iShare: meIsA ? raw.a_privacy : raw.b_privacy,
    theyShare: meIsA ? raw.b_privacy : raw.a_privacy,
    initiatedByMe: raw.initiated_by === myId,
    createdAt: raw.created_at,
    acceptedAt: raw.accepted_at
  };
}

/** Load all of my connections (any status). */
export async function loadConnections(): Promise<Connection[]> {
  if (!supabase) return [];
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return [];

  // Pull both the connections table and the partner-email lookup in parallel.
  // The email lookup populates `emailCache`, which normalize() reads to show
  // a real address instead of "user ABCD1234" for accepted-partner rows.
  const [connRes] = await Promise.all([
    supabase.from("connections").select("*").order("created_at", { ascending: false }),
    refreshEmailCache()
  ]);
  if (connRes.error) throw connRes.error;
  return (connRes.data as RawConnection[]).map((r) => normalize(r, user.id));
}

/**
 * Send a partner invite. If the partner has an account, they get an in-app
 * pending invite + email. If not, they get an email with an invite code +
 * a pending-connection that auto-claims when they sign up.
 */
export async function inviteByEmail(
  email: string,
  note?: string
): Promise<{
  connectionId: string;
  status: "pending" | "accepted";
  partnerExisted: boolean;
  inviteCode: string | null;
  already?: boolean;
}> {
  const res = await apiFetch("/api/connect/invite", {
    method: "POST",
    body: JSON.stringify({ email, note })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `Invite failed (${res.status})`);
  }
  const body = (await res.json()) as {
    connection_id: string;
    status: "pending" | "accepted";
    partner_existed: boolean;
    invite_code: string | null;
    already?: boolean;
  };
  // Cache email for the partner if we got a user_id back
  return {
    connectionId: body.connection_id,
    status: body.status,
    partnerExisted: body.partner_existed,
    inviteCode: body.invite_code,
    already: body.already
  };
}

/** Accept a pending invite addressed to me. */
export async function acceptConnection(connectionId: string): Promise<void> {
  const res = await apiFetch("/api/connect/accept", {
    method: "POST",
    body: JSON.stringify({ connection_id: connectionId })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `Accept failed (${res.status})`);
  }
}

/** Delete the connection (= disconnect, mutual + immediate). */
export async function disconnect(connectionId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.from("connections").delete().eq("id", connectionId);
  if (error) throw error;
}

/** Update MY side's privacy tier toward a partner. */
export async function setMyPrivacy(
  connection: Connection,
  newTier: PrivacyTier
): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  // Need to know whether I'm user_a or user_b to update the right column.
  // Re-fetch the raw row to find out (simpler than threading state).
  const { data: raw, error: loadErr } = await supabase
    .from("connections")
    .select("user_a, user_b")
    .eq("id", connection.id)
    .single();
  if (loadErr) throw loadErr;
  const sideColumn = raw.user_a === user.id ? "a_privacy" : "b_privacy";

  const { error } = await supabase
    .from("connections")
    .update({ [sideColumn]: newTier })
    .eq("id", connection.id);
  if (error) throw error;
}

/** Pull a partner's day summary using the SECURITY DEFINER RPC. */
export interface PartnerDaySummary {
  partner_id: string;
  summary_date: string;
  privacy: PrivacyTier;
  meal_count: number;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  nutrients: Record<string, number> | null;
}

export async function loadPartnerDaySummary(
  partnerId: string,
  day: string
): Promise<PartnerDaySummary | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc("partner_day_summary", {
    p_partner_id: partnerId,
    p_day: day
  });
  if (error) throw error;
  const rows = (data ?? []) as PartnerDaySummary[];
  return rows[0] ?? null;
}
